import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import mqtt, { type MqttClient } from 'mqtt';

import { useGenerateEmqxToken } from '@/domain/auth';
import { isEphemeralHostname } from '@/utils/environmentByHostname';

import { attachEmqxHandlers, emqxConnectOptions } from './emqxClient';

const mqttDisabledForEphemeralEnvironment = isEphemeralHostname();

export interface EmqxConnection {
    emqxClient: MqttClient | null;
    isEmqxConnected: boolean;
    /** The current client, for callbacks that must not close over a stale one. */
    emqxClientRef: MutableRefObject<MqttClient | null>;
    isEmqxConnectedRef: MutableRefObject<boolean>;
}

/**
 * The EMQX connection: fetches the JWT, connects, retries, refreshes the token before it
 * expires and closes the client on unmount. Disabled on ephemeral preview environments.
 */
export function useEmqxConnection(): EmqxConnection {
    const [emqxClient, setEmqxClient] = useState<MqttClient | null>(null);
    const [isEmqxConnected, setIsEmqxConnected] = useState(false);

    // Refs to track current client instances for cleanup (avoid stale closures)
    const emqxClientRef = useRef<MqttClient | null>(null);
    // Refs to track connection state for token refresh logic
    const isEmqxConnectedRef = useRef<boolean>(false);
    // Refs to track latest tokens (avoid stale closures in token refresh)
    const emqxTokenRef = useRef<string | null>(null);
    // Ref to track EMQX username (must match JWT token username)
    const emqxUsernameRef = useRef<string>('optoscale_frontend');
    // Refs to track connection attempts (prevent multiple simultaneous attempts)
    const emqxConnectingRef = useRef<boolean>(false);
    const emqxRetryCountRef = useRef<number>(0);
    const emqxRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Refs to store stable function references (for recursive calls and cross-references)
    const connectToEmqxRef = useRef<() => Promise<void>>(() => Promise.resolve());
    const fetchEmqxTokenRef = useRef<() => Promise<void>>(() => Promise.resolve());

    const [emqxToken, setEmqxToken] = useState<string | null>(null);

    const generateEmqxToken = useGenerateEmqxToken();

    // Stable function to fetch EMQX JWT token
    const fetchEmqxToken = useCallback(async () => {
        if (mqttDisabledForEphemeralEnvironment) {
            return;
        }
        try {
            const { token, username } = await generateEmqxToken();
            setEmqxToken(token);
            emqxTokenRef.current = token;
            // Store the username returned by backend for use in connection (must match JWT token)
            if (username) {
                emqxUsernameRef.current = username;
            }
        } catch (error) {
            console.error('[EMQX] Failed to generate JWT token:', error);
        }
    }, [generateEmqxToken]);

    // Keep fetchEmqxToken ref updated for use in recursive calls
    fetchEmqxTokenRef.current = fetchEmqxToken;

    // Stable disconnect function for EMQX
    const disconnectEmqx = useCallback(() => {
        if (emqxClientRef.current) {
            emqxClientRef.current.end();
            emqxClientRef.current = null;
            setEmqxClient(null);
            setIsEmqxConnected(false);
            isEmqxConnectedRef.current = false;
        }
    }, []);

    // Stable connect function for EMQX - uses refs for recursive calls
    const connectToEmqx = useCallback(async () => {
        try {
            // Prevent multiple simultaneous connection attempts
            if (emqxConnectingRef.current) {
                console.log('[EMQX] Connection attempt already in progress, skipping');
                return;
            }

            // If already connected, don't reconnect
            if (isEmqxConnectedRef.current) {
                console.log('[EMQX] Already connected, skipping');
                return;
            }

            // Disconnect existing connection if any
            if (emqxClientRef.current) {
                disconnectEmqx();
            }

            // Use ref to get latest token (avoids stale closure issues)
            const token = emqxTokenRef.current || '';

            // Don't attempt connection without a token
            if (!token) {
                console.warn('[EMQX] No token available, skipping connection');
                return;
            }

            // Mark as connecting
            emqxConnectingRef.current = true;

            const { url, clientId, options } = emqxConnectOptions(token, emqxUsernameRef.current);

            const mqttClient = mqtt.connect(url, options);
            setEmqxClient(mqttClient);
            emqxClientRef.current = mqttClient;

            // Set up connection timeout handler
            const connectionTimeout = setTimeout(() => {
                if (!isEmqxConnectedRef.current) {
                    console.warn('[EMQX] Connection timeout - closing client');
                    mqttClient.end();
                }
            }, 35 * 1000); // Slightly longer than connectTimeout

            attachEmqxHandlers(mqttClient, {
                url,
                clientId,
                token,
                connectionTimeout,
                refs: {
                    emqxClientRef,
                    isEmqxConnectedRef,
                    emqxUsernameRef,
                    emqxConnectingRef,
                    emqxRetryCountRef,
                    emqxRetryTimeoutRef,
                    connectToEmqxRef,
                    fetchEmqxTokenRef,
                },
                setIsEmqxConnected,
            });
        } catch (error) {
            emqxConnectingRef.current = false;
            console.error('[EMQX] Failed to connect to MQTT:', error);
        }
    }, [disconnectEmqx]);

    // Keep connectToEmqx ref updated for recursive calls
    connectToEmqxRef.current = connectToEmqx;

    // Initialize tokens and set up refresh interval
    useEffect(() => {
        if (mqttDisabledForEphemeralEnvironment) {
            console.info('[EMQX] Ephemeral environment (uat-pr/preview-pr) — MQTT disabled');
            return;
        }
        // Only fetch EMQX token initially (HiveMQ token will be lazy loaded on fallback)
        fetchEmqxToken();

        // Token refresh before token expiry
        const tokenInterval = setInterval(
            async () => {
                // Refresh EMQX token and reconnect if connected
                const wasEmqxConnected = isEmqxConnectedRef.current;
                await fetchEmqxToken();
                console.log('[EMQX] Token refreshed');
                // Reconnect with new token if previously connected
                // Token ref is updated in fetchEmqxToken, so connectToEmqx will use latest token
                if (wasEmqxConnected) {
                    console.log('[EMQX] Reconnecting with new token...');
                    connectToEmqx();
                }
            },
            59.5 * 60 * 1000
        ); // 59.5 minutes

        return () => {
            clearInterval(tokenInterval);

            // Clear any pending retry timeouts
            if (emqxRetryTimeoutRef.current) {
                clearTimeout(emqxRetryTimeoutRef.current);
                emqxRetryTimeoutRef.current = null;
            }

            // Cleanup connections on unmount using refs to avoid stale closures
            if (emqxClientRef.current) {
                emqxClientRef.current.end();
                emqxClientRef.current = null;
            }
        };
    }, [fetchEmqxToken, connectToEmqx]);

    // Connect to EMQX when token is available
    useEffect(() => {
        if (mqttDisabledForEphemeralEnvironment) {
            return;
        }
        // Use ref for connection check to avoid dependency on isEmqxConnected (prevents loops)
        if (emqxToken && !isEmqxConnectedRef.current) {
            connectToEmqx();
        }
    }, [emqxToken, connectToEmqx]);

    return { emqxClient, isEmqxConnected, emqxClientRef, isEmqxConnectedRef };
}
